import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Loader2,
  User,
  Palette,
  CreditCard,
  Check,
  Mail,
  Globe,
  FileText,
  Download,
  RefreshCcw,
  Server,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  { id: 'free', name: 'Free', price: 0, galleries: 3, photos: 50, storage: '1 GB' },
  { id: 'lite', name: 'Lite', price: 29, galleries: 10, photos: 200, storage: '10 GB' },
  { id: 'pro', name: 'Pro', price: 79, galleries: 50, photos: 500, storage: '50 GB' },
  { id: 'studio', name: 'Studio', price: 149, galleries: '∞', photos: '∞', storage: '200 GB' },
  { id: 'enterprise', name: 'Enterprise', price: 299, galleries: '∞', photos: '∞', storage: '∞' },
];

const COLORS = [
  { name: 'White', value: '#FAFAFA' },
  { name: 'Gold', value: '#D4AF37' },
  { name: 'Rose', value: '#E8B4B8' },
  { name: 'Teal', value: '#008080' },
  { name: 'Purple', value: '#6B5B95' },
];

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(user?.branding || {});
  const [emailTemplates, setEmailTemplates] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    fetchEmailTemplates();
    fetchInvoices();
  }, []);

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-templates`);
      setEmailTemplates(response.data.templates);
    } catch (error) {
      console.error('Failed to fetch email templates');
    }
  };

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data.invoices);
    } catch (error) {
      console.error('Failed to fetch invoices');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleTemplateUpdate = async (templateType, field, value) => {
    const updated = {
      ...emailTemplates,
      [templateType]: {
        ...emailTemplates[templateType],
        [field]: value
      }
    };
    setEmailTemplates(updated);
  };

  const saveEmailTemplate = async (templateType) => {
    try {
      const template = emailTemplates[templateType];
      await axios.put(`${API}/email-templates/${templateType}`, {
        subject: template.subject_pl || template.subject,
        body: template.body_pl || template.body,
        enabled: template.enabled !== false
      });
      toast.success(language === 'pl' ? 'Szablon zapisany' : 'Template saved');
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleBrandingUpdate = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings/branding`, branding);
      updateUser({ branding });
      toast.success(t('settings.saved'));
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    try {
      await axios.put(`${API}/settings/language?language=${lang}`);
    } catch (error) {
      console.error('Failed to save language preference');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/settings" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        <h1 className="text-3xl font-bold mb-8">{t('settings.title')}</h1>

        <Tabs defaultValue="account" className="space-y-6">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="account" className="data-[state=active]:bg-secondary">
              <User className="w-4 h-4 mr-2" />
              {t('settings.account')}
            </TabsTrigger>
            <TabsTrigger value="branding" className="data-[state=active]:bg-secondary">
              <Palette className="w-4 h-4 mr-2" />
              {t('settings.branding')}
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="data-[state=active]:bg-secondary">
              <Globe className="w-4 h-4 mr-2" />
              Portfolio
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-secondary">
              <CreditCard className="w-4 h-4 mr-2" />
              {t('settings.billing')}
            </TabsTrigger>
            <TabsTrigger value="emails" className="data-[state=active]:bg-secondary">
              <Mail className="w-4 h-4 mr-2" />
              {language === 'pl' ? 'Emaile' : 'Emails'}
            </TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-secondary">
              <FileText className="w-4 h-4 mr-2" />
              {language === 'pl' ? 'Faktury' : 'Invoices'}
            </TabsTrigger>
            <TabsTrigger value="smtp" className="data-[state=active]:bg-secondary">
              <Server className="w-4 h-4 mr-2" />
              SMTP
            </TabsTrigger>
          </TabsList>

          {/* Account Tab */}
          <TabsContent value="account">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle>{t('settings.account')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>{t('auth.name')}</Label>
                      <Input value={user?.name || ''} disabled className="h-12" />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('auth.email')}</Label>
                      <Input value={user?.email || ''} disabled className="h-12" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('settings.language')}</Label>
                    <div className="flex gap-3">
                      <Button
                        variant={language === 'pl' ? 'default' : 'outline'}
                        onClick={() => handleLanguageChange('pl')}
                        data-testid="lang-pl-btn"
                      >
                        🇵🇱 Polski
                      </Button>
                      <Button
                        variant={language === 'en' ? 'default' : 'outline'}
                        onClick={() => handleLanguageChange('en')}
                        data-testid="lang-en-btn"
                      >
                        🇬🇧 English
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Subdomain Card */}
              <SubdomainSettings language={language} />
            </motion.div>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle>{t('settings.branding')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>{t('settings.logo')}</Label>
                    <Input
                      placeholder="https://example.com/logo.png"
                      value={branding.logo_url || ''}
                      onChange={(e) => setBranding(prev => ({ ...prev, logo_url: e.target.value }))}
                      className="h-12"
                      data-testid="branding-logo-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('settings.primaryColor')}</Label>
                    <div className="flex gap-3 flex-wrap">
                      {COLORS.map(color => (
                        <button
                          key={color.value}
                          onClick={() => setBranding(prev => ({ ...prev, primary_color: color.value }))}
                          className={`w-10 h-10 rounded-sm border-2 transition-all ${
                            branding.primary_color === color.value ? 'border-primary scale-110' : 'border-border'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                      <Input
                        type="color"
                        value={branding.primary_color || '#FAFAFA'}
                        onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="w-10 h-10 p-0 border-0 cursor-pointer"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('settings.fontFamily')}</Label>
                    <div className="flex gap-3 flex-wrap">
                      {['Manrope', 'Inter', 'Playfair Display', 'DM Sans'].map(font => (
                        <Button
                          key={font}
                          variant={branding.font_family === font ? 'default' : 'outline'}
                          onClick={() => setBranding(prev => ({ ...prev, font_family: font }))}
                          style={{ fontFamily: font }}
                        >
                          {font}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{language === 'pl' ? 'Tytuł galerii' : 'Gallery Title'}</Label>
                    <Input
                      placeholder={language === 'pl' ? 'np. Studio Foto Kowalski' : 'e.g. Kowalski Photo Studio'}
                      value={branding.gallery_title || ''}
                      onChange={(e) => setBranding(prev => ({ ...prev, gallery_title: e.target.value }))}
                      className="h-12"
                    />
                    <p className="text-xs text-muted-foreground">
                      {language === 'pl' ? 'Tytuł wyświetlany w publicznych galeriach' : 'Title shown in public galleries'}
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <Label>{language === 'pl' ? 'Ukryj "Powered by Focus Photo"' : 'Hide "Powered by Focus Photo"'}</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        {language === 'pl' ? 'Dostępne w planach Pro i wyższych' : 'Available in Pro plans and above'}
                      </p>
                    </div>
                    <Button
                      variant={branding.hide_storybook_badge ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBranding(prev => ({ ...prev, hide_storybook_badge: !prev.hide_storybook_badge }))}
                    >
                      {branding.hide_storybook_badge
                        ? (language === 'pl' ? 'Ukryte' : 'Hidden')
                        : (language === 'pl' ? 'Widoczne' : 'Visible')}
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('settings.customDomain')}</Label>
                    <Input
                      placeholder="gallery.yoursite.com"
                      value={branding.custom_domain || ''}
                      onChange={(e) => setBranding(prev => ({ ...prev, custom_domain: e.target.value }))}
                      className="h-12"
                      data-testid="branding-domain-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {language === 'pl' ? 'Dostępne w planach Pro i wyższych' : 'Available in Pro plans and above'}
                    </p>
                  </div>

                  <Button onClick={handleBrandingUpdate} disabled={loading} data-testid="save-branding-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {t('common.save')}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <PortfolioSettings language={language} />
            </motion.div>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Current Plan */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle>{t('settings.currentPlan')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 bg-secondary rounded-sm">
                    <div>
                      <p className="font-bold text-lg capitalize">{user?.plan || 'Free'}</p>
                      <p className="text-sm text-muted-foreground">
                        {language === 'pl' ? 'Aktywny plan' : 'Active plan'}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-primary-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Available Plans */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle>{t('settings.upgradePlan')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {PLANS.map(plan => (
                      <div 
                        key={plan.id}
                        className={`p-4 rounded-sm border transition-all ${
                          user?.plan === plan.id 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <h3 className="font-bold mb-1">{plan.name}</h3>
                        <p className="text-2xl font-bold mb-3">
                          {plan.price > 0 ? `${plan.price} zł` : 'Free'}
                          {plan.price > 0 && <span className="text-sm font-normal text-muted-foreground">/mies.</span>}
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                          <li>{plan.galleries} {language === 'pl' ? 'galerii' : 'galleries'}</li>
                          <li>{plan.photos} {language === 'pl' ? 'zdjęć/galeria' : 'photos/gallery'}</li>
                          <li>{plan.storage}</li>
                        </ul>
                        <Button 
                          variant={user?.plan === plan.id ? 'outline' : 'default'}
                          size="sm"
                          className="w-full"
                          disabled={user?.plan === plan.id}
                          data-testid={`plan-${plan.id}-btn`}
                        >
                          {user?.plan === plan.id 
                            ? (language === 'pl' ? 'Aktualny' : 'Current')
                            : (language === 'pl' ? 'Wybierz' : 'Select')
                          }
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Emails Tab */}
          <TabsContent value="emails">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle>{language === 'pl' ? 'Szablony emaili' : 'Email Templates'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  {Object.entries(emailTemplates).map(([key, template]) => (
                    <div key={key} className="p-4 border border-border rounded-sm space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold capitalize">
                          {key === 'gallery_ready' && (language === 'pl' ? 'Galeria gotowa' : 'Gallery Ready')}
                          {key === 'reminder' && (language === 'pl' ? 'Przypomnienie' : 'Reminder')}
                          {key === 'expiring' && (language === 'pl' ? 'Galeria wygasa' : 'Gallery Expiring')}
                          {key === 'new_comment' && (language === 'pl' ? 'Nowy komentarz' : 'New Comment')}
                        </h4>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => saveEmailTemplate(key)}
                        >
                          {language === 'pl' ? 'Zapisz' : 'Save'}
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{language === 'pl' ? 'Temat (PL)' : 'Subject (PL)'}</Label>
                        <Input
                          value={template.subject_pl || ''}
                          onChange={(e) => handleTemplateUpdate(key, 'subject_pl', e.target.value)}
                          className="h-10"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{language === 'pl' ? 'Treść (PL)' : 'Body (PL)'}</Label>
                        <Textarea
                          value={template.body_pl || ''}
                          onChange={(e) => handleTemplateUpdate(key, 'body_pl', e.target.value)}
                          rows={4}
                          className="resize-none"
                        />
                      </div>
                      
                      <div className="text-xs text-muted-foreground">
                        {language === 'pl' ? 'Dostępne zmienne' : 'Available variables'}: {'{client_name}'}, {'{gallery_name}'}, {'{gallery_url}'}, {'{photographer_name}'}
                      </div>
                    </div>
                  ))}
                  
                  {Object.keys(emailTemplates).length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{language === 'pl' ? 'Ładowanie szablonów...' : 'Loading templates...'}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <Card className="bg-card border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>{language === 'pl' ? 'Historia faktur' : 'Invoice History'}</CardTitle>
                  <Button variant="outline" size="sm" onClick={fetchInvoices}>
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    {language === 'pl' ? 'Odśwież' : 'Refresh'}
                  </Button>
                </CardHeader>
                <CardContent>
                  {loadingInvoices ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : invoices.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-2 font-medium text-muted-foreground">
                              {language === 'pl' ? 'Numer' : 'Number'}
                            </th>
                            <th className="text-left py-3 px-2 font-medium text-muted-foreground">
                              {language === 'pl' ? 'Klient' : 'Client'}
                            </th>
                            <th className="text-left py-3 px-2 font-medium text-muted-foreground">
                              {language === 'pl' ? 'Data' : 'Date'}
                            </th>
                            <th className="text-right py-3 px-2 font-medium text-muted-foreground">
                              {language === 'pl' ? 'Akcja' : 'Action'}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((invoice) => (
                            <tr key={invoice.id} className="border-b border-border/50 hover:bg-secondary/30">
                              <td className="py-3 px-2 font-medium">{invoice.invoice_number}</td>
                              <td className="py-3 px-2 text-muted-foreground">{invoice.client_name}</td>
                              <td className="py-3 px-2 text-muted-foreground">
                                {new Date(invoice.created_at).toLocaleDateString('pl-PL')}
                              </td>
                              <td className="py-3 px-2 text-right">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => downloadInvoice(invoice.id, invoice.invoice_number)}
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  PDF
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{language === 'pl' ? 'Brak faktur' : 'No invoices'}</p>
                      <p className="text-sm mt-2">
                        {language === 'pl' 
                          ? 'Faktury są generowane automatycznie po zakończeniu zamówień' 
                          : 'Invoices are generated automatically after orders are completed'}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
          {/* SMTP Tab */}
          <TabsContent value="smtp">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <SmtpSettings language={language} />
            </motion.div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// SMTP Settings Component
function SmtpSettings({ language }) {
  const [config, setConfig] = useState({
    host: '', port: 587, username: '', password: '',
    from_email: '', from_name: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/settings/smtp`);
      setConfig({
        host: res.data.host || '',
        port: res.data.port || 587,
        username: res.data.username || '',
        password: '',
        from_email: res.data.from_email || '',
        from_name: res.data.from_name || '',
      });
      setVerified(res.data.verified || false);
    } catch (error) {
      console.error('Failed to fetch SMTP config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.host || !config.username || !config.password || !config.from_email) {
      toast.error(language === 'pl' ? 'Wypelnij wszystkie wymagane pola' : 'Fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/settings/smtp`, config);
      setVerified(false);
      toast.success(language === 'pl' ? 'Konfiguracja SMTP zapisana' : 'SMTP configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/settings/smtp/test`);
      if (res.data.ok) {
        setVerified(true);
        toast.success(language === 'pl' ? 'Email testowy wyslany!' : 'Test email sent!');
      } else {
        toast.error(res.data.error || 'Test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="w-5 h-5" />
          {language === 'pl' ? 'Konfiguracja SMTP' : 'SMTP Configuration'}
          {verified && (
            <Badge variant="default" className="ml-2">
              <Check className="w-3 h-3 mr-1" />
              {language === 'pl' ? 'Zweryfikowany' : 'Verified'}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">
          {language === 'pl'
            ? 'Skonfiguruj wlasny serwer SMTP, aby wysylac wiadomosci ze swojego adresu email.'
            : 'Configure your own SMTP server to send messages from your email address.'}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Serwer SMTP' : 'SMTP Host'} *</Label>
            <Input
              value={config.host}
              onChange={(e) => setConfig(c => ({ ...c, host: e.target.value }))}
              placeholder="smtp.gmail.com"
            />
          </div>
          <div className="space-y-2">
            <Label>Port *</Label>
            <Input
              type="number"
              value={config.port}
              onChange={(e) => setConfig(c => ({ ...c, port: parseInt(e.target.value) || 587 }))}
              placeholder="587"
            />
          </div>
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Login' : 'Username'} *</Label>
            <Input
              value={config.username}
              onChange={(e) => setConfig(c => ({ ...c, username: e.target.value }))}
              placeholder="your@email.com"
            />
          </div>
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Haslo' : 'Password'} *</Label>
            <Input
              type="password"
              value={config.password}
              onChange={(e) => setConfig(c => ({ ...c, password: e.target.value }))}
              placeholder="****"
            />
          </div>
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Adres nadawcy' : 'From Email'} *</Label>
            <Input
              value={config.from_email}
              onChange={(e) => setConfig(c => ({ ...c, from_email: e.target.value }))}
              placeholder="studio@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Nazwa nadawcy' : 'From Name'}</Label>
            <Input
              value={config.from_name}
              onChange={(e) => setConfig(c => ({ ...c, from_name: e.target.value }))}
              placeholder="Studio Foto"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            {language === 'pl' ? 'Zapisz' : 'Save'}
          </Button>
          <Button variant="outline" onClick={handleTest} disabled={testing}>
            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {language === 'pl' ? 'Wyslij testowy email' : 'Send Test Email'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Subdomain Settings Component
function SubdomainSettings({ language }) {
  const [subdomain, setSubdomain] = useState('');
  const [publicUrl, setPublicUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSubdomain();
  }, []);

  const fetchSubdomain = async () => {
    try {
      const response = await axios.get(`${API}/settings/subdomain`);
      setSubdomain(response.data.subdomain || '');
      setPublicUrl(response.data.public_url || '');
    } catch (error) {
      console.error('Failed to fetch subdomain');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!subdomain.trim()) {
      toast.error(language === 'pl' ? 'Podaj subdomenę' : 'Enter subdomain');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings/subdomain`, { subdomain: subdomain.toLowerCase().trim() });
      setPublicUrl(response.data.public_url);
      toast.success(language === 'pl' ? 'Subdomena zapisana!' : 'Subdomain saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="w-5 h-5" />
          {language === 'pl' ? 'Twoja subdomena' : 'Your Subdomain'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {language === 'pl' 
            ? 'Ustaw własny adres dla swojej strony rezerwacji i portfolio.'
            : 'Set a custom address for your booking page and portfolio.'}
        </p>
        
        <div className="flex gap-2 items-center">
          <Input 
            value={subdomain}
            onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="twoja-nazwa"
            className="max-w-xs"
          />
          <span className="text-muted-foreground">.focusphoto.pl</span>
        </div>
        
        {publicUrl && (
          <div className="p-3 bg-secondary rounded-sm">
            <p className="text-sm text-muted-foreground mb-1">
              {language === 'pl' ? 'Twoje linki:' : 'Your links:'}
            </p>
            <div className="space-y-1 text-sm">
              <p>
                <span className="text-muted-foreground">{language === 'pl' ? 'Portfolio' : 'Portfolio'}:</span>{' '}
                <a href={`/p/${subdomain}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  {window.location.origin}/p/{subdomain}
                </a>
              </p>
              <p>
                <span className="text-muted-foreground">{language === 'pl' ? 'Rezerwacje' : 'Bookings'}:</span>{' '}
                <a href={`/booking/${subdomain}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  {window.location.origin}/booking/{subdomain}
                </a>
              </p>
            </div>
          </div>
        )}
        
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
          {language === 'pl' ? 'Zapisz' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  );
}


// Portfolio Settings Component
function PortfolioSettings({ language }) {
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_subtitle: 'Profesjonalna fotografia',
    about_text: '',
    show_galleries: true,
    show_services: true,
    show_contact: true,
    theme: 'dark',
    accent_color: '#FAFAFA',
    social_links: {}
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subdomain, setSubdomain] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [portfolioRes, subdomainRes] = await Promise.all([
        axios.get(`${API}/settings/portfolio`),
        axios.get(`${API}/settings/subdomain`)
      ]);
      setSettings(portfolioRes.data);
      setSubdomain(subdomainRes.data.subdomain || '');
    } catch (error) {
      console.error('Failed to fetch portfolio settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/portfolio`, settings);
      toast.success(language === 'pl' ? 'Ustawienia zapisane!' : 'Settings saved!');
    } catch (error) {
      toast.error(language === 'pl' ? 'Błąd zapisu' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const PORTFOLIO_THEMES = [
    { id: 'dark', name: language === 'pl' ? 'Ciemny' : 'Dark', bg: '#000' },
    { id: 'light', name: language === 'pl' ? 'Jasny' : 'Light', bg: '#fff' },
    { id: 'warm', name: language === 'pl' ? 'Ciepły' : 'Warm', bg: '#fef3c7' },
    { id: 'cool', name: language === 'pl' ? 'Chłodny' : 'Cool', bg: '#0f172a' }
  ];

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Subdomain Card */}
      <SubdomainSettings language={language} />

      {/* Portfolio Content */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {language === 'pl' ? 'Treść portfolio' : 'Portfolio Content'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Hero Title */}
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Tytuł główny' : 'Main Title'}</Label>
            <Input
              value={settings.hero_title}
              onChange={(e) => setSettings(s => ({ ...s, hero_title: e.target.value }))}
              placeholder={language === 'pl' ? 'Twoje imię lub nazwa studia' : 'Your name or studio name'}
            />
          </div>

          {/* Hero Subtitle */}
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Podtytuł' : 'Subtitle'}</Label>
            <Input
              value={settings.hero_subtitle}
              onChange={(e) => setSettings(s => ({ ...s, hero_subtitle: e.target.value }))}
              placeholder={language === 'pl' ? 'np. Fotografia ślubna' : 'e.g. Wedding Photography'}
            />
          </div>

          {/* About Text */}
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'O mnie' : 'About Me'}</Label>
            <Textarea
              value={settings.about_text}
              onChange={(e) => setSettings(s => ({ ...s, about_text: e.target.value }))}
              placeholder={language === 'pl' ? 'Krótki opis Twojej działalności...' : 'Short description of your work...'}
              rows={4}
            />
          </div>

          {/* Social Links */}
          <div className="space-y-4">
            <Label>{language === 'pl' ? 'Media społecznościowe' : 'Social Media'}</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                value={settings.social_links?.instagram || ''}
                onChange={(e) => setSettings(s => ({ 
                  ...s, 
                  social_links: { ...s.social_links, instagram: e.target.value }
                }))}
                placeholder="https://instagram.com/twoj_profil"
              />
              <Input
                value={settings.social_links?.facebook || ''}
                onChange={(e) => setSettings(s => ({ 
                  ...s, 
                  social_links: { ...s.social_links, facebook: e.target.value }
                }))}
                placeholder="https://facebook.com/twoja_strona"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Portfolio Appearance */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            {language === 'pl' ? 'Wygląd portfolio' : 'Portfolio Appearance'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Theme Selection */}
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Motyw' : 'Theme'}</Label>
            <div className="flex gap-3 flex-wrap">
              {PORTFOLIO_THEMES.map(theme => (
                <button
                  key={theme.id}
                  onClick={() => setSettings(s => ({ ...s, theme: theme.id }))}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    settings.theme === theme.id 
                      ? 'border-primary' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div 
                    className="w-16 h-12 rounded mb-2 border"
                    style={{ backgroundColor: theme.bg }}
                  />
                  <span className="text-sm">{theme.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Accent Color */}
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Kolor akcentu' : 'Accent Color'}</Label>
            <div className="flex items-center gap-4">
              <Input
                type="color"
                value={settings.accent_color || '#FAFAFA'}
                onChange={(e) => setSettings(s => ({ ...s, accent_color: e.target.value }))}
                className="w-16 h-10 p-1 cursor-pointer"
              />
              <span className="text-sm text-muted-foreground">{settings.accent_color}</span>
            </div>
          </div>

          {/* Visibility Options */}
          <div className="space-y-4">
            <Label>{language === 'pl' ? 'Widoczność sekcji' : 'Section Visibility'}</Label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.show_galleries}
                  onChange={(e) => setSettings(s => ({ ...s, show_galleries: e.target.checked }))}
                  className="w-4 h-4"
                />
                <span>{language === 'pl' ? 'Pokaż galerie' : 'Show galleries'}</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.show_services}
                  onChange={(e) => setSettings(s => ({ ...s, show_services: e.target.checked }))}
                  className="w-4 h-4"
                />
                <span>{language === 'pl' ? 'Pokaż usługi' : 'Show services'}</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.show_contact}
                  onChange={(e) => setSettings(s => ({ ...s, show_contact: e.target.checked }))}
                  className="w-4 h-4"
                />
                <span>{language === 'pl' ? 'Pokaż kontakt' : 'Show contact'}</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-4">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {language === 'pl' ? 'Zapisz ustawienia' : 'Save Settings'}
            </Button>
            
            {subdomain && (
              <a 
                href={`/p/${subdomain}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline text-sm"
              >
                {language === 'pl' ? 'Zobacz portfolio →' : 'View portfolio →'}
              </a>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
}
